import { getAgentApi } from '@/gql';
import user from '@/stores/user';
import { useEffect, useState } from 'react';

export interface UseAgentExtraInfoResult {
    trendingIndex: number;
    coBuildPoints: number;
    coBuildUserCount: number;
    coBuildPointsByUser: number;
    countUserChatsToday: number;
    countUserSubmittedKnowledgesToday: number;
    loading: boolean;
    error: Error | null;
    refetch: () => Promise<{
        trendingIndex: number;
        coBuildPoints: number;
        coBuildUserCount: number;
        coBuildPointsByUser: number;
        countUserChatsToday: number;
        countUserSubmittedKnowledgesToday: number;
    }>;
}

export const useAgentExtraInfo = (uId: string): UseAgentExtraInfoResult => {
    const [trendingIndex, setTrendingIndex] = useState<number>(0);
    const [coBuildPoints, setCoBuildPoints] = useState<number>(0);
    const [coBuildUserCount, setCoBuildUserCount] = useState<number>(0);
    const [coBuildPointsByUser, setCoBuildPointsByUser] = useState<number>(0);
    const [countUserChatsToday, setCountUserChatsToday] = useState<number>(0);
    const [
        countUserSubmittedKnowledgesToday,
        setCountUserSubmittedKnowledgesToday,
    ] = useState<number>(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const { userId } = user;

    const getAgentExtraInfo = async () => {
        if (!uId || !userId) {
            setTrendingIndex(0);
            setCoBuildPoints(0);
            setCoBuildUserCount(0);
            setCoBuildPointsByUser(0);
            setCountUserChatsToday(0);
            setCountUserSubmittedKnowledgesToday(0);
            return {
                trendingIndex: 0,
                coBuildPoints: 0,
                coBuildUserCount: 0,
                coBuildPointsByUser: 0,
                countUserChatsToday: 0,
                countUserSubmittedKnowledgesToday: 0,
            };
        }
        try {
            setLoading(true);
            setError(null);
            const api = getAgentApi();
            const response = await api.agentExtraInfo({ agentUid: uId });
            if (response) {
                setTrendingIndex(response.trendingIndex ?? 0);
                setCoBuildPoints(response.coBuildPoints ?? 0);
                setCoBuildUserCount(response.coBuildUserCount ?? 0);
                setCoBuildPointsByUser(response.coBuildPointsByUser ?? 0);
                setCountUserChatsToday(response.countUserChatsToday ?? 0);
                setCountUserSubmittedKnowledgesToday(
                    response.countUserSubmittedKnowledgesToday ?? 0,
                );
                return {
                    trendingIndex: response.trendingIndex ?? 0,
                    coBuildPoints: response.coBuildPoints ?? 0,
                    coBuildUserCount: response.coBuildUserCount ?? 0,
                    coBuildPointsByUser: response.coBuildPointsByUser ?? 0,
                    countUserChatsToday: response.countUserChatsToday ?? 0,
                    countUserSubmittedKnowledgesToday:
                        response.countUserSubmittedKnowledgesToday ?? 0,
                };
            } else {
                setTrendingIndex(0);
                setCoBuildPoints(0);
                setCoBuildUserCount(0);
                setCoBuildPointsByUser(0);
                setCountUserChatsToday(0);
                setCountUserSubmittedKnowledgesToday(0);
                return {
                    trendingIndex: 0,
                    coBuildPoints: 0,
                    coBuildUserCount: 0,
                    coBuildPointsByUser: 0,
                    countUserChatsToday: 0,
                    countUserSubmittedKnowledgesToday: 0,
                };
            }
        } catch (err) {
            console.error('Failed to fetch agent extra info:', err);
            setError(
                err instanceof Error
                    ? err
                    : new Error('Failed to fetch agent extra info'),
            );
            setTrendingIndex(0);
            setCoBuildPoints(0);
            setCoBuildUserCount(0);
            setCoBuildPointsByUser(0);
            setCountUserChatsToday(0);
            setCountUserSubmittedKnowledgesToday(0);
            return {
                trendingIndex: 0,
                coBuildPoints: 0,
                coBuildUserCount: 0,
                coBuildPointsByUser: 0,
                countUserChatsToday: 0,
                countUserSubmittedKnowledgesToday: 0,
            };
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getAgentExtraInfo();
    }, [uId, userId]);

    return {
        trendingIndex,
        coBuildPoints,
        coBuildUserCount,
        coBuildPointsByUser,
        countUserChatsToday,
        countUserSubmittedKnowledgesToday,
        loading,
        error,
        refetch: getAgentExtraInfo,
    };
};
